import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, MagnifyingGlass, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const empty = { name: "", company: "", email: "", phone: "", address: "", notes: "" };

export default function Customers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/customers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setLoading(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Failed to create"); }
    finally { setLoading(false); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete customer? This won't remove past work orders/invoices.")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    load();
  };

  const filtered = query
    ? list.filter((c) =>
        [c.name, c.company, c.email, c.phone].join(" ").toLowerCase().includes(query.trim().toLowerCase())
      )
    : [];
  const showResults = query.trim().length > 0;

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle={`${list.length} total · search to reveal`}
        actions={
          <button className="btn-primary flex items-center gap-2" onClick={() => setOpen(true)} data-testid="add-customer-btn">
            <Plus size={16} /> Add Customer
          </button>
        }
      />

      <div className="surface p-3 mb-4 flex items-center gap-2">
        <MagnifyingGlass size={18} className="text-slate-400 ml-2" />
        <input
          data-testid="customer-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, company, email, phone…"
          className="bg-transparent flex-1 outline-none text-sm text-slate-900 placeholder:text-slate-400 py-2"
          autoFocus
        />
        {query && (
          <button
            className="text-slate-400 hover:text-slate-700 px-2 text-xs font-mono uppercase tracking-wider"
            onClick={() => setQuery("")}
            data-testid="customer-search-clear"
          >
            Clear
          </button>
        )}
      </div>

      {!showResults ? (
        <div className="surface p-10 text-center fade-up" data-testid="customer-search-empty">
          <MagnifyingGlass size={40} className="text-slate-300 mx-auto mb-3" weight="duotone" />
          <div className="text-slate-700 font-medium mb-1">Type a name, company, email, or phone to find a customer</div>
          <div className="text-sm text-slate-500">
            {list.length > 0
              ? `${list.length} customers in your database — hidden by default to keep this page clean.`
              : "No customers yet. Click Add Customer to create your first one."}
          </div>
        </div>
      ) : (
        <div className="surface overflow-x-auto fade-up" data-testid="customer-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th><th>Company</th><th>Email</th><th>Phone</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-10">No matches for &ldquo;{query}&rdquo;.</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} onClick={() => (window.location.href = `/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}>
                  <td className="text-slate-900 font-medium">{c.name}</td>
                  <td>{c.company || "—"}</td>
                  <td className="font-mono text-xs">{c.email || "—"}</td>
                  <td className="font-mono text-xs">{c.phone || "—"}</td>
                  <td className="text-right">
                    <button className="text-slate-400 hover:text-red-400 p-1" onClick={(e) => remove(e, c.id)} data-testid={`delete-customer-${c.id}`}>
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="customer-dialog">
          <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <Field label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cust-name" />
            <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="cust-company" />
            <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="cust-email" />
            <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="cust-phone" />
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Address</Label>
              <Textarea data-testid="cust-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Notes</Label>
              <Textarea data-testid="cust-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={loading} data-testid="save-customer-btn">
              {loading ? "Saving…" : "Save Customer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);
